import { POST_LOGIN_REDIRECT_KEY, isSafeRedirectPath } from '@/shared/components/layout/ProtectedRoute'

/**
 * The deep link a person was following before being sent to sign in.
 *
 * ProtectedRoute stashes it for the org-scoped tree. The identity-only pages
 * (/create-studio, /no-studio) sit OUTSIDE that tree — they must resolve an
 * identity, not an org session — so they stash it themselves, and login has to
 * honour it for a session that has no studio at all. Without that, the
 * "sign in to create your studio" email sends someone to /create-studio, login
 * decides they have no membership, and drops them on /no-studio instead: the
 * one page that cannot tell them why they are not where they asked to be.
 */

export function rememberIntendedPath(path: string): void {
  if (!isSafeRedirectPath(path)) return
  try {
    sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, path)
  } catch {
    // A private-browsing quota error costs a deep link, never a login.
  }
}

/** Reads and clears the stashed path. Null when there wasn't a usable one. */
export function takeIntendedPath(): string | null {
  let intended: string | null = null
  try {
    intended = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY)
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY)
  } catch {
    return null
  }
  return isSafeRedirectPath(intended) ? intended : null
}

/**
 * Every route an identity-only session can actually use — the mirror image of
 * SecurityConfig's identity-only surface. Anything else in the app needs an org
 * session, so following a deep link there would only produce 403s.
 */
const IDENTITY_ONLY_PATHS = ['/create-studio', '/no-studio']

/** Where to send a proven identity that holds no membership. */
export function identityOnlyDestination(intended: string | null): string {
  return intended && IDENTITY_ONLY_PATHS.some(p => intended === p || intended.startsWith(p + '?'))
    ? intended
    : '/no-studio'
}
